import { AlertIconDark } from "@images/svgIcons/alertIcon"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { deleteSubscriptionYupValidation } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useDeleteBoxSubscription } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { formatDateWithHyphenUnbreakable } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { Info } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type BoxSubscriptionDeleteModalRef = {
  open: () => void
}

export type BoxSubscriptionDeleteModalProps = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity | null
  setSubscriptionSelected: (subscription: BoxSubscriptionDetailsEntity | null) => void
  ref: React.Ref<BoxSubscriptionDeleteModalRef>
}

export function BoxSubscriptionDeleteModal({
  memberId,
  subscription,
  setSubscriptionSelected,
  ref,
}: BoxSubscriptionDeleteModalProps) {
  const theme = useTheme()
  const { deleteSubscription, isDeleteSubscriptionPending } = useDeleteBoxSubscription(memberId)
  const [showModal, setShowModal] = useState(false)
  const toast = useToast()
  const endDateOptions = [
    {
      value: 0,
      label:
        word("box.subscription.delete.modal.formik.currentPeriod") +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).format("LL") : ""),
    },
    ,
    {
      value: 1,
      label:
        word("box.subscription.delete.modal.formik.nextMonth") +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(1, "months").format("LL") : ""),
    },
    {
      value: 2,
      label:
        word("box.subscription.delete.modal.formik.inMonths", { count: 2 }) +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(2, "months").format("LL") : ""),
    },
    {
      value: 3,
      label:
        word("box.subscription.delete.modal.formik.inMonths", { count: 3 }) +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(3, "months").format("LL") : ""),
    },
    {
      value: 4,
      label:
        word("box.subscription.delete.modal.formik.inMonths", { count: 4 }) +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(4, "months").format("LL") : ""),
    },
    {
      value: 5,
      label:
        word("box.subscription.delete.modal.formik.inMonths", { count: 5 }) +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(5, "months").format("LL") : ""),
    },
    {
      value: 6,
      label:
        word("box.subscription.delete.modal.formik.inMonths", { count: 6 }) +
        " - " +
        (subscription ? dayjs(subscription.subscriptionEndDate).add(6, "months").format("LL") : ""),
    },
  ]

  const formik = useFormik({
    initialValues: {
      cancelAtPeriodEnd: false,
      endPeriodInMonths: undefined,
    },
    validationSchema: deleteSubscriptionYupValidation(),
    onSubmit: async () => {
      if (subscription) {
        await deleteSubscription({
          subscriptionId: subscription.id,
          cancelAtPeriodEnd: formik.values.cancelAtPeriodEnd,
          endPeriodInMonths: formik.values.endPeriodInMonths,
        })
          .then(() => {
            toast.show({
              severity: "success",
              summary: word("box.subscription.delete.modal.toast.success"),
            })
            onHide()
          })
          .catch(() => {
            toast.show({
              severity: "error",
              summary: word("box.subscription.delete.modal.toast.error"),
            })
          })
      }
    },
  })

  const onHide = useCallback(() => {
    formik.resetForm()
    setSubscriptionSelected(null)
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "480px" }}>
      <div className="flex flex-column gap-5">
        {/* HEADER */}
        <div className="flex flex-column flex-wrap pt-4 gap-2 justify-content-center align-content-center text-center">
          <div className="flex w-full justify-content-center align-content-center">
            <AlertIconDark color={theme.error} />
          </div>
          <HUText fontStyle="TL" color={theme.mainWhite}>
            {word("box.subscription.delete.modal.header.title")}
          </HUText>
        </div>
        {subscription?.minimumCommitmentDate ? (
          <HUCardAlert
            title={`${word("box.subscription.delete.modal.alert.title")} ${formatDateWithHyphenUnbreakable(dayjs(subscription.minimumCommitmentDate).format("L"))}`}
            icon={<Info size={45} className="mx-2" />}
            className=" py-2 border-round-2xl align-items-center"
            titleFontStyle="BL"
            style={{ whiteSpace: "break-spaces" }}
          />
        ) : null}
        <HUText
          fontStyle="BM"
          className="flex justify-content-center align-content-center text-center"
          color={theme.neutralColor600}
        >
          {word("box.subscription.delete.modal.header.subtitle")}
        </HUText>
        {/* CONTENT */}
        <div className="flex gap-3">
          <div
            className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl
                ${formik.getFieldProps("cancelAtPeriodEnd").value ? "cursor-pointer border-neutralColor-700 " : "border-electricPurple-400"}`}
            onClick={() => (
              formik.setFieldValue("cancelAtPeriodEnd", false), formik.setFieldValue("endPeriodInMonths", undefined)
            )}
          >
            <HUText fontStyle="TS" className="text-center" color={theme.mainWhite}>
              {word("box.subscription.delete.modal.content.now")}
            </HUText>
          </div>
          <div
            className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl
                ${formik.getFieldProps("cancelAtPeriodEnd").value ? "border-electricPurple-400" : "cursor-pointer border-neutralColor-700"}`}
            onClick={() => formik.setFieldValue("cancelAtPeriodEnd", true)}
          >
            <HUText fontStyle="TS" className="  text-center" color={theme.mainWhite}>
              {word("box.subscription.delete.modal.content.endDate")}
            </HUText>
          </div>
        </div>
        <FormikInput
          formikStyle={{ flexGrow: 1 }}
          placeHolder={word("box.subscription.delete.modal.formik.placeholder")}
          isRequiredInput
          getFieldProps={formik.getFieldProps}
          name="endPeriodInMonths"
          setFieldValue={formik.setFieldValue}
          isInvalid={isFormFieldInValid("endPeriodInMonths", formik)}
          error={getFormErrorMessage("endPeriodInMonths", formik)}
          type="dropdown"
          disabled={formik.getFieldProps("cancelAtPeriodEnd").value ? false : true}
          dropdownProps={{
            value: formik.values.endPeriodInMonths,
            options: endDateOptions,
          }}
        />

        {/* BUTTON */}
        <div className="mb-3 flex flex-row gap-2 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={onHide}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.remove.standard")}
            onClick={formik.submitForm}
            className="w-full"
            disabled={isDeleteSubscriptionPending}
          />
        </div>
      </div>
    </HUDialog>
  )
}
